"use client"

import axios from "axios"
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import Delete from "../../../assets/svgs/Recycle Bin.svg"
import ConfirmationModalBov from "../../../components/ConfirmationModals/ConfirmationModalBov"
import useAuth from "../../../hooks/useAuth"
import { BASE_URL } from "../../../utils/Element"

const statusArray = {
  1: "new_lead",
  3: "executed_loi",
  4: "under_contract",
  5: "bov_presented",
  6: "exclusive_listing",
}

const StageDispositionDashboard = ({ activeTab, selectedUser }) => {
  const [loading, setLoading] = useState(true)
  const [config] = useAuth()
  const [categories, setCategories] = useState([
    { key: 1, title: "Qualified Prospect", pipelines: [] },
    { key: 5, title: "BOV Presented", pipelines: [] },
    { key: 6, title: "Exclusive Listing", pipelines: [] },
    { key: 3, title: "Executed LOI", pipelines: [] },
    { key: 4, title: "Under Contract", pipelines: [] },
  ])

  const [showContractModal, setShowContractModal] = useState(false)
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [showConfirmModalBov, setShowConfirmModalBov] = useState(false)
  const [showExcecutedLoi, setShowExcecutedLoi] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const [selectedStatus, setSelectedStatus] = useState(null)
  const [pendingMove, setPendingMove] = useState(null)
  const [pipelineDetails, setPipelineDetails] = useState(null)

  // Drag state
  const [draggedPipelineId, setDraggedPipelineId] = useState(null)

  const fetchPipelineById = async (id) => {
    try {
      const response = await axios.get(`${BASE_URL}/getby-id-pipeline-global/${id}`, config)
      setPipelineDetails(response.data)
      return response.data
    } catch (error) {
      console.error("Error fetching pipeline by ID:", error)
      return null
    }
  }

  const PipelineStageAcquisition = async () => {
    try {
      setLoading(true)
      let url = `${BASE_URL}/dashboard-pipeline-stage-disposition?active_tab=${activeTab}`

      if (selectedUser && selectedUser.value) {
        url += `&show_user_id=${selectedUser.value}`
      }

      const response = await axios.get(url, config)
      const pipelineData = response?.data?.pipeline_stage?.pipeline_status_disposition_data || {}

      const formattedCategories = categories.map((cat) => {
        const statusKey = statusArray[cat.key]
        return {
          ...cat,
          pipelines: pipelineData[statusKey]
            ? pipelineData[statusKey].map((item) => ({
              id: item.id,
              name: item.contact_name,
              contact_id: item.contact_id,
              lastContacted: item.last_contact_update_date,
              potentialgcl: item.potential_gcl_format,
              advisorcommission: item.potential_advisor_commission_format,
              pipeline: item.associate_name,
              lead_id: item.lead_id,
            }))
            : [],
        }
      })

      setCategories(formattedCategories)
    } catch (error) {
      console.error("Error fetching pipelines:", error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    PipelineStageAcquisition()
  }, [activeTab, selectedUser])

  const updatePipelineStatus = async (pipelineId, fromStage, toStage) => {
    try {
      await axios.put(
        `${BASE_URL}/dashboard-pipeline-stage-status/${pipelineId}`,
        { id: pipelineId, fromStage, toStage },
        config,
      )
    } catch (error) {
      console.error("Error updating pipeline status:", error)
      throw error
    }
  }

  const updatePipelineColumnOrder = async (pipelines) => {
    const columnUpdatePayload = pipelines.map((item, index) => ({
      id: item.id,
      column_no: index,
    }))

    try {
      await axios.put(`${BASE_URL}/pipeline-column-update`, JSON.stringify(columnUpdatePayload), {
        ...config,
        headers: { ...config.headers, "Content-Type": "application/json" },
      })
    } catch (error) {
      console.error("Error updating pipeline column order:", error)
      throw error
    }
  }

  const applyPendingMove = async () => {
    if (!pendingMove) return

    const { pipelineId, fromCategoryIndex, toCategoryIndex, insertIndex } = pendingMove

    setCategories((prev) => {
      const newCategories = prev.map((cat) => ({
        ...cat,
        pipelines: [...cat.pipelines],
      }))

      let movedPipeline = null
      for (let cat of newCategories) {
        const idx = cat.pipelines.findIndex((p) => p.id === pipelineId)
        if (idx !== -1) {
          movedPipeline = cat.pipelines.splice(idx, 1)[0]
          break
        }
      }

      if (!movedPipeline) return prev

      newCategories[toCategoryIndex].pipelines.splice(insertIndex, 0, movedPipeline)

      return newCategories
    })

    try {
      const fromStage = categories[fromCategoryIndex].key
      const toStage = categories[toCategoryIndex].key
      await updatePipelineStatus(pipelineId, fromStage, toStage)

      // If you want to update column order in target column after move
      setCategories((current) => {
        const targetPipelines = current[toCategoryIndex].pipelines
        updatePipelineColumnOrder(targetPipelines).catch(console.error)
        return current
      })
    } catch (error) {
      PipelineStageAcquisition() // rollback
    } finally {
      setPendingMove(null)
    }
  }

  const onDragStart = (e, pipelineId, fromCategoryIndex, pipelineIndex) => {
    setDraggedPipelineId(pipelineId)
    e.dataTransfer.setData("pipelineId", pipelineId)
    e.dataTransfer.setData("fromCategory", fromCategoryIndex)
    e.dataTransfer.setData("pipelineIndex", pipelineIndex)
  }

  const onDragEnd = () => {
    setDraggedPipelineId(null)
  }

  const onDragOver = (e) => {
    e.preventDefault()
  }

  const onDrop = (e, toCategoryIndex) => {
    e.preventDefault()

    const pipelineId = parseInt(e.dataTransfer.getData("pipelineId"), 10)
    const fromCategoryIndex = parseInt(e.dataTransfer.getData("fromCategory"), 10)

    if (isNaN(pipelineId) || isNaN(fromCategoryIndex)) return

    // Calculate insert position (same logic as StageDashboard)
    const dropY = e.clientY
    const container = e.currentTarget.querySelector(".pipeline-container")
    if (!container) return

    const children = Array.from(container.children)
    let insertIndex = children.length

    for (let i = 0; i < children.length; i++) {
      const box = children[i].getBoundingClientRect()
      if (dropY < box.top + box.height / 2) {
        insertIndex = i
        break
      }
    }

    // Special case: Qualified Prospect → BOV Presented
    if (fromCategoryIndex === 0 && toCategoryIndex === 1) {
      setPendingMove({ pipelineId, fromCategoryIndex, toCategoryIndex, insertIndex })
      setSelectedId(pipelineId)
      // You can call fetchPipelineById here if needed for modal
      // fetchPipelineById(pipelineId)
      setShowConfirmModalBov(true)
      return
    }

    // Normal move (same pattern as StageDashboard)
    setCategories((prev) => {
      const newCategories = prev.map((cat) => ({
        ...cat,
        pipelines: [...cat.pipelines],
      }))

      let movedPipeline = null
      for (let cat of newCategories) {
        const idx = cat.pipelines.findIndex((p) => p.id === pipelineId)
        if (idx !== -1) {
          movedPipeline = cat.pipelines.splice(idx, 1)[0]
          break
        }
      }

      if (!movedPipeline) return prev

      newCategories[toCategoryIndex].pipelines.splice(insertIndex, 0, movedPipeline)

      return newCategories
    })

    // Backend update
    const fromStage = categories[fromCategoryIndex].key
    const toStage = categories[toCategoryIndex].key

    updatePipelineStatus(pipelineId, fromStage, toStage).catch((err) => {
      console.error("Backend status update failed", err)
      PipelineStageAcquisition() // rollback
    })

    // Optional: update column order in target column
    setCategories((current) => {
      const targetPipelines = current[toCategoryIndex].pipelines
      updatePipelineColumnOrder(targetPipelines).catch(console.error)
      return current
    })

    setDraggedPipelineId(null)
  }

  const onDeleteDrop = async (e) => {
    e.preventDefault()
    const pipelineId = parseInt(e.dataTransfer.getData("pipelineId"), 10)
    if (!pipelineId) return

    try {
      await axios.delete(`${BASE_URL}/delete-pipeline/${pipelineId}`, config)
      PipelineStageAcquisition()
    } catch (err) {
      console.error("Delete failed", err)
    } finally {
      setDraggedPipelineId(null)
    }
  }

  return (
    <div className="flex flex-col w-full h-screen bg-gray-100 p-4 overflow-hidden">
      {/* Columns */}
      <div className="flex w-full space-x-3 flex-1 mb-4 overflow-y-auto">
        {categories.map((category, categoryIndex) => (
          <div
            key={categoryIndex}
            className="flex-1 flex flex-col"
            onDragOver={onDragOver}
            onDrop={(e) => onDrop(e, categoryIndex)}
          >
            <div className="green-bg-H text-white px-2 py-1 rounded-t-md text-center text-sm font-medium">
              {category.title}
            </div>

            <div className="bg-white flex-1 overflow-y-auto border border-gray-200 rounded-b-md pipeline-container">
              {category.pipelines.map((pipeline, pipelineIndex) => (
                <div
                  key={pipeline.id}
                  className={`p-2 border-b border-gray-100 hover:bg-gray-50 cursor-pointer text-sm ${draggedPipelineId === pipeline.id ? "opacity-50" : ""
                    }`}
                  draggable
                  onDragStart={(e) =>
                    onDragStart(e, pipeline.id, categoryIndex, pipelineIndex)
                  }
                  onDragEnd={onDragEnd}
                >
                  <p className="font-medium text-sm">
                    <Link to={`/contact/${pipeline.contact_id}`}>
                      {pipeline.name}
                    </Link>
                  </p>
                  <p className="text-xs text-gray-500">
                    Last Contacted: {pipeline.lastContacted || "—"}
                  </p>
                  <p className="text-xs text-gray-500">
                    Potential GCI: ${pipeline.potentialgcl || "—"}
                  </p>
                   <p className="text-xs text-gray-500">
                    Potential Advisor Commission : ${pipeline.advisorcommission }
                  </p>
                  <p className="text-xs text-gray-500">
                    Pipeline: {pipeline.pipeline || "—"}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Delete zone */}
      <div
        className="w-full flex justify-center py-2"
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDeleteDrop}
      >
        <button className="flex items-center gap-2 border border-red-400 text-red-500 px-4 py-1.5 rounded-full bg-white hover:bg-red-50 transition-all text-sm">
          <img src={Delete} alt="delete" className="w-4 h-4" />
          Remove from Pipeline
        </button>
      </div>

      <ConfirmationModalBov
        showModal={showConfirmModalBov}
        onClose={() => {
          setShowConfirmModalBov(false)
          setPendingMove(null)
          setSelectedId(null)
        }}
        pipelineDetails={pipelineDetails}
        onCallApi={async () => {
          setShowConfirmModalBov(false)
          await applyPendingMove()
          // You can refresh after confirmed move
          PipelineStageAcquisition()
        }}
        id={selectedId}
      />
    </div>
  )
}

export default StageDispositionDashboard